import { Livewire, Alpine } from '../../vendor/livewire/livewire/dist/livewire.esm';
import EmblaCarousel from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import AutoHeight from 'embla-carousel-auto-height';
import AutoScroll from 'embla-carousel-auto-scroll';
import ClassNames from 'embla-carousel-class-names';
import Fade from 'embla-carousel-fade';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { scroll } from 'motion';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import homeGlobeLandUrl from 'world-atlas/land-110m.json?url';


function uiText(key, fallback) {
    return window.NorthwestAlpsUiText?.[key] || fallback;
}

// ── Lazy-loaded heavy libraries ──────────────────────────────────────────────
// Leaflet (+ Geoman editing plugin) and the homepage globe are only needed on a
// handful of pages. They are dynamically imported on demand so the shared bundle
// stays lean for the average editorial visitor. Each loader caches its promise so
// concurrent callers share a single network/parse pass.
let L = null;
let leafletPromise = null;
let geomanPromise = null;
let d3Geo = null;
let topojsonClient = null;
let geoGlobePromise = null;
let homeGlobeTopologyPromise = null;
let lucideCatalogPromise = null;
let lucideIconNames = ['map-pin'];
let lucideIcons = {
    'map-pin': '<path d="M20 10c0 4.9-5.5 10.3-7.4 12a1 1 0 0 1-1.2 0C9.5 20.3 4 14.9 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/>',
};

function loadLucideCatalog() {
    if (!lucideCatalogPromise) {
        lucideCatalogPromise = import('./lucide-icons').then((catalog) => {
            lucideIconNames = catalog.lucideIconNames;
            lucideIcons = catalog.lucideIcons;

            return catalog;
        });
    }

    return lucideCatalogPromise;
}

function loadLeaflet() {
    if (!leafletPromise) {
        leafletPromise = Promise.all([
            import('leaflet'),
            import('leaflet/dist/leaflet.css'),
            import('../css/maps.css'),
        ]).then(([leafletModule]) => {
            L = leafletModule.default;

            return L;
        });
    }

    return leafletPromise;
}

function loadGeoman() {
    if (!geomanPromise) {
        // Geoman augments the Leaflet `L` namespace, so Leaflet must resolve first.
        geomanPromise = loadLeaflet().then(() => Promise.all([
            import('@geoman-io/leaflet-geoman-free'),
            import('@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css'),
        ]));
    }

    return geomanPromise;
}

function loadGeoGlobe() {
    if (!geoGlobePromise) {
        geoGlobePromise = Promise.all([
            import('d3-geo'),
            import('topojson-client'),
        ]).then(([geoModule, topojsonModule]) => {
            d3Geo = geoModule;
            topojsonClient = topojsonModule;

            return { d3Geo, topojsonClient };
        });
    }

    return geoGlobePromise;
}

function sendAnalyticsEvent(payload) {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

    if (!token || !payload?.event || navigator.webdriver) {
        return;
    }

    const body = JSON.stringify(payload);

    if (navigator.sendBeacon) {
        const data = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon('/analytics/events?_token=' + encodeURIComponent(token), data);

        return;
    }

    fetch('/analytics/events', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': token,
            Accept: 'application/json',
        },
        body,
        keepalive: true,
    }).catch(() => {});
}

document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-analytics-event]');

    if (!target) {
        return;
    }

    sendAnalyticsEvent({
        event: target.dataset.analyticsEvent,
        label: target.dataset.analyticsLabel || target.textContent?.trim()?.slice(0, 120),
        category: target.dataset.analyticsCategory,
        target: target.dataset.analyticsTarget || target.getAttribute('href'),
        content_type: target.dataset.analyticsContentType,
        content_id: target.dataset.analyticsContentId,
        component: target.dataset.analyticsComponent,
    });
});

Alpine.store('theme', {
    value: localStorage.getItem('theme') ?? 'system',

    init() {
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
            if (this.value === 'system') {
                this.apply();
            }
        });
    },

    apply() {
        const isDark =
            this.value === 'dark' ||
            (this.value === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
        document.documentElement.classList.toggle('dark', isDark);
        document.cookie = 'theme-resolved=' + (isDark ? 'dark' : 'light') + '; path=/; max-age=31536000; SameSite=Lax';
    },

    set(value) {
        this.value = value;
        if (value === 'system') {
            localStorage.removeItem('theme');
        } else {
            localStorage.setItem('theme', value);
        }
        this.apply();
    },

    toggle() {
        this.set(document.documentElement.classList.contains('dark') ? 'light' : 'dark');
    },

    get isDark() {
        return document.documentElement.classList.contains('dark');
    },
});

Alpine.data('publicSearch', (config = {}) => ({
    query: String(config.query || ''),
    results: Array.isArray(config.results) ? config.results : [],
    activeIndex: -1,
    loading: false,
    abortController: null,
    get hasQuery() {
        return this.query.trim().length >= 2;
    },
    move(delta) {
        if (this.results.length === 0) {
            this.activeIndex = -1;
            return;
        }

        this.activeIndex = (this.activeIndex + delta + this.results.length) % this.results.length;
        this.$el.querySelectorAll('[data-public-search-result]')[this.activeIndex]?.scrollIntoView({ block: 'nearest' });
    },
    openActive() {
        const result = this.results[this.activeIndex];

        if (!result?.url) {
            return false;
        }

        this.navigate(result.url);

        return true;
    },
    navigate(target) {
        this.$dispatch('public-search-close');

        if (window.Livewire?.navigate) {
            window.Livewire.navigate(target);
            return;
        }

        window.location.assign(target);
    },
    submit() {
        if (this.openActive()) {
            return;
        }

        const url = new URL(config.searchUrl, window.location.origin);
        const query = this.query.trim();

        if (query) {
            url.searchParams.set('q', query);
        }

        this.navigate(url.toString());
    },
    async search() {
        this.activeIndex = -1;

        if (!config.showResults || !this.hasQuery) {
            this.abortController?.abort();
            this.results = [];
            this.loading = false;
            return;
        }

        this.abortController?.abort();
        this.abortController = new AbortController();
        this.loading = true;

        try {
            const url = new URL(config.endpoint, window.location.origin);
            url.searchParams.set('q', this.query.trim());
            url.searchParams.set('locale', config.locale);
            url.searchParams.set('limit', config.limit);

            const response = await fetch(url, {
                headers: { Accept: 'application/json' },
                signal: this.abortController.signal,
            });

            if (!response.ok) {
                throw new Error(`Search request failed with ${response.status}`);
            }

            const payload = await response.json();
            this.results = Array.isArray(payload.results) ? payload.results : [];
        } catch (error) {
            if (error.name !== 'AbortError') {
                this.results = [];
            }
        } finally {
            if (!this.abortController?.signal.aborted) {
                this.loading = false;
            }
        }
    },
}));

Alpine.data('publicReactions', (config = {}) => ({
    counts: { ...(config.counts || {}) },
    loading: false,
    justReacted: false,
    lastReactedType: null,
    async react(type) {
        if (this.loading) {
            return;
        }

        this.loading = true;

        try {
            const token = document.querySelector('meta[name="csrf-token"]')?.content;
            const response = await fetch(config.endpoint, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': token || '',
                },
                body: JSON.stringify({
                    model_type: config.modelType,
                    model_id: config.modelId,
                    locale: config.locale,
                    type,
                }),
            });

            if (!response.ok) {
                throw new Error(`Reaction request failed with ${response.status}`);
            }

            const payload = await response.json();
            this.counts = { ...(payload.counts || this.counts) };
            this.lastReactedType = type;
            this.justReacted = true;
        } finally {
            this.loading = false;
        }
    },
}));



Alpine.data('cmsMediaGallery', () => ({
    lightbox: null,

    init() {
        this.lightbox = createCmsLightbox({
            gallery: this.$el,
            children: 'a[href][data-pswp-width][data-pswp-height]',
        });

        this.lightbox.init();
    },

    destroy() {
        this.lightbox?.destroy();
        this.lightbox = null;
    },
}));

const cmsLightboxes = new WeakMap();
const cmsSingleLightboxLinks = new WeakSet();
const cmsCarousels = new WeakMap();
const cmsParallaxCleanups = new WeakMap();

const lucideCloseSvg = '<span aria-hidden="true" class="cms-pswp-icon-wrap"><svg class="cms-pswp-icon" viewBox="0 0 24 24"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></span>';
const lucideChevronLeftSvg = '<span aria-hidden="true" class="cms-pswp-icon-wrap"><svg class="cms-pswp-icon" viewBox="0 0 24 24"><path d="m15 18-6-6 6-6"/></svg></span>';
const lucideChevronRightSvg = '<span aria-hidden="true" class="cms-pswp-icon-wrap"><svg class="cms-pswp-icon" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"/></svg></span>';
const lucideZoomSvg = '<span aria-hidden="true" class="cms-pswp-icon-wrap"><svg class="cms-pswp-icon" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/><path d="M8 11h6"/><path class="pswp__zoom-icn-bar-v" d="M11 8v6"/></svg></span>';

function createCmsLightbox(options = {}) {
    return new PhotoSwipeLightbox({
        mainClass: 'pswp--cms-ui',
        bgOpacity: 1,
        showHideAnimationType: 'fade',
        padding: { top: 76, right: 24, bottom: 76, left: 24 },
        closeSVG: lucideCloseSvg,
        zoomSVG: lucideZoomSvg,
        arrowPrevSVG: lucideChevronLeftSvg,
        arrowNextSVG: lucideChevronRightSvg,
        closeTitle: uiText('lightboxClose', 'Close'),
        zoomTitle: 'Zoom',
        arrowPrevTitle: uiText('lightboxPreviousImage', 'Previous image'),
        arrowNextTitle: uiText('lightboxNextImage', 'Next image'),
        pswpModule: () => import('photoswipe'),
        ...options,
    });
}

function cmsGroupedLightboxScopes(root = document) {
    const selector = '.cms-gallery, .cms-image-grid';
    const scopes = root.matches?.(selector)
        ? [root, ...root.querySelectorAll(selector)]
        : [...root.querySelectorAll(selector)];

    return scopes.filter((scope) => {
        const parentScope = scope.parentElement?.closest(selector);

        return ! parentScope || ! scopes.includes(parentScope);
    });
}

function cmsPhotoSwipeItemFromLink(link, options = {}) {
    const image = link.querySelector('img');
    const alt = image?.alt || link.getAttribute('aria-label') || link.dataset.pswpCaption || '';
    const includeElement = options.includeElement ?? true;

    return {
        src: link.href,
        width: Number(link.dataset.pswpWidth || image?.getAttribute('width') || 1600),
        height: Number(link.dataset.pswpHeight || image?.getAttribute('height') || 1067),
        alt,
        ...(includeElement ? { element: link } : {}),
    };
}

function openSingleCmsLightbox(link) {
    const lightbox = createCmsLightbox({
        mainClass: 'pswp--cms-ui pswp--cms-single',
        arrowPrev: false,
        arrowNext: false,
        arrowKeys: false,
        counter: false,
        loop: false,
        dataSource: [cmsPhotoSwipeItemFromLink(link)],
    });

    lightbox.on('close', () => {
        window.setTimeout(() => lightbox.destroy(), 0);
    });

    lightbox.init();
    lightbox.loadAndOpen(0);
}

function openGalleryCmsLightbox(gallery, currentLink) {
    const links = [...gallery.querySelectorAll('a[href][data-pswp-width][data-pswp-height]')];
    const index = Math.max(0, links.indexOf(currentLink));
    const hasMultiplePhotos = links.length > 1;
    const lightbox = createCmsLightbox({
        mainClass: `pswp--cms-ui pswp--cms-gallery${hasMultiplePhotos ? '' : ' pswp--cms-single'}`,
        arrowPrev: hasMultiplePhotos,
        arrowNext: hasMultiplePhotos,
        arrowKeys: hasMultiplePhotos,
        counter: hasMultiplePhotos,
        loop: hasMultiplePhotos,
        dataSource: links.map((link) => cmsPhotoSwipeItemFromLink(link, { includeElement: false })),
    });

    lightbox.on('close', () => {
        window.setTimeout(() => lightbox.destroy(), 0);
    });

    lightbox.init();
    lightbox.loadAndOpen(index);
}

function initCmsLightboxes(root = document) {
    cmsGroupedLightboxScopes(root).forEach((gallery) => {
        if (cmsLightboxes.has(gallery) || ! gallery.querySelector('a[href][data-pswp-width][data-pswp-height]')) {
            return;
        }

        const controller = new AbortController();

        gallery.addEventListener('click', (event) => {
            const link = event.target?.closest?.('a[href][data-pswp-width][data-pswp-height]');

            if (! link || ! gallery.contains(link)) {
                return;
            }

            event.preventDefault();
            openGalleryCmsLightbox(gallery, link);
        }, { signal: controller.signal });

        cmsLightboxes.set(gallery, controller);
    });

    root.querySelectorAll('a[href][data-pswp-width][data-pswp-height]').forEach((link) => {
        if (cmsSingleLightboxLinks.has(link) || link.closest('.cms-gallery, .cms-image-grid')) {
            return;
        }

        cmsSingleLightboxLinks.add(link);
        link.addEventListener('click', (event) => {
            event.preventDefault();
            openSingleCmsLightbox(link);
        });
    });
}

function normalizeCmsParallaxImages(root = document) {
    root.querySelectorAll('[data-cms-parallax]').forEach((figure) => {
        figure.classList?.remove('cms-lightbox-scope');

        figure.querySelectorAll('a[data-pswp-width], a[data-pswp-height], a[data-pswp-caption]').forEach((anchor) => {
            const image = anchor.querySelector('img');

            if (image) {
                anchor.replaceWith(image);

                return;
            }

            anchor.removeAttribute('data-pswp-width');
            anchor.removeAttribute('data-pswp-height');
            anchor.removeAttribute('data-pswp-caption');
        });
    });
}

function initCmsGalleryCarousels(root = document) {
    root.querySelectorAll('.cms-gallery--carousel').forEach((gallery) => {
        const viewport = gallery.querySelector('[data-cms-gallery-carousel-viewport]');

        if (cmsCarousels.has(gallery) || ! viewport) {
            return;
        }

        const layout = gallery.dataset.cmsGalleryLayout || 'carousel';
        const plugins = [];

        if (layout === 'carousel-autoplay') {
            plugins.push(Autoplay({ delay: 3500, stopOnInteraction: true }));
        }

        if (layout === 'carousel-fade') {
            plugins.push(Fade());
        }

        const api = EmblaCarousel(viewport, {
            align: 'start',
            loop: true,
        }, plugins);

        gallery.querySelector('[data-cms-gallery-prev]')?.addEventListener('click', () => api.scrollPrev());
        gallery.querySelector('[data-cms-gallery-next]')?.addEventListener('click', () => api.scrollNext());
        cmsCarousels.set(gallery, api);
    });
}

function initCmsParallax(root = document) {
    root.querySelectorAll('[data-cms-parallax]').forEach((figure) => {
        const image = figure.querySelector('img');

        if (cmsParallaxCleanups.has(figure) || figure.dataset.cmsParallaxWaiting === 'true' || ! image) {
            return;
        }

        figure.dataset.cmsParallaxWaiting = 'true';
        const activate = () => {
            if (!figure.isConnected || cmsParallaxCleanups.has(figure)) {
                return;
            }

            delete figure.dataset.cmsParallaxWaiting;
            const strength = Number(figure.dataset.cmsParallaxStrength || 90);
            const cleanup = scroll((progress) => {
                const y = (progress - 0.5) * strength;
                image.style.transform = `translate3d(0, ${y}px, 0) scale(1.08)`;
            }, {
                target: figure,
                offset: ['start end', 'end start'],
            });

            cmsParallaxCleanups.set(figure, cleanup);
        };

        if (window.scrollY > 0) {
            activate();
        } else {
            window.addEventListener('scroll', activate, { once: true, passive: true });
        }
    });
}

function initCmsCuriosities(root = document) {
    root.querySelectorAll('[data-cms-curiosity]').forEach((element) => {
        if (element.dataset.cmsCuriosityReady === 'true') {
            return;
        }

        element.dataset.cmsCuriosityReady = 'true';
        const open = () => {
            const title = element.dataset.cmsCuriosityTitle || element.querySelector('.cms-curiosity-title')?.textContent || uiText('curiosity', 'Curiosity');
            const content = element.querySelector('.cms-curiosity-modal-content')?.innerHTML || '';
            const overlay = document.createElement('div');

            overlay.className = 'cms-curiosity-overlay';
            overlay.innerHTML = `
                <div class="cms-curiosity-dialog" role="dialog" aria-modal="true">
                    <button class="cms-curiosity-close" type="button" aria-label="${escapeHtml(uiText('close', 'Close'))}">×</button>
                    <h2>${escapeHtml(title)}</h2>
                    <div>${content}</div>
                </div>
            `;

            const hadPageLock = document.documentElement.classList.contains('overflow-hidden');
            document.documentElement.classList.add('overflow-hidden');

            let closed = false;
            const onEscape = (event) => {
                if (event.key === 'Escape') {
                    close();
                }
            };
            const close = () => {
                if (closed) {
                    return;
                }

                closed = true;
                document.removeEventListener('keydown', onEscape);
                overlay.classList.add('is-closing');

                if (! hadPageLock) {
                    document.documentElement.classList.remove('overflow-hidden');
                }

                window.setTimeout(() => overlay.remove(), 180);
            };

            overlay.addEventListener('click', (event) => {
                if (event.target === overlay || event.target.closest('.cms-curiosity-close')) {
                    close();
                }
            });
            document.addEventListener('keydown', onEscape);

            document.body.appendChild(overlay);
            overlay.querySelector('button')?.focus();
        };

        element.addEventListener('click', open);
        element.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                open();
            }
        });
    });
}

function initLegacyYoutubeEmbeds(root = document) {
    root.querySelectorAll('.youtube-embed[data-service="youtube"][data-id]').forEach((element) => {
        if (['true', 'prompt'].includes(element.dataset.youtubeReady)) {
            return;
        }

        const videoId = (element.dataset.id || '').trim();

        if (! /^[A-Za-z0-9_-]{6,}$/.test(videoId)) {
            return;
        }

        const title = element.dataset.title || 'Video YouTube';
        const playButton = document.createElement('button');
        playButton.type = 'button';
        playButton.className = 'youtube-embed__play';
        playButton.setAttribute('aria-label', `${title}: ${uiText('youtubePlay', 'Riproduci video')}`);
        playButton.innerHTML = `${lucideControlIcon('play')}<span>${uiText('youtubePlay', 'Riproduci video')}</span>`;

        playButton.addEventListener('click', () => {
            const iframe = document.createElement('iframe');
            iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1`;
            iframe.title = title;
            iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
            iframe.allowFullscreen = true;
            iframe.referrerPolicy = 'strict-origin-when-cross-origin';

            element.replaceChildren(iframe);
            element.dataset.youtubeReady = 'true';
        }, { once: true });

        element.replaceChildren(playButton);
        element.dataset.youtubeReady = 'prompt';
    });
}

function initDeferredImages(root = document) {
    root.querySelectorAll('img[data-deferred-src]:not([data-deferred-ready])').forEach((image) => {
        image.dataset.deferredReady = 'true';

        const load = () => {
            if (image.dataset.deferredSrcset) {
                image.srcset = image.dataset.deferredSrcset;
            }

            image.src = image.dataset.deferredSrc;
            delete image.dataset.deferredSrc;
            delete image.dataset.deferredSrcset;
        };

        if (!('IntersectionObserver' in window)) {
            load();
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) {
                return;
            }

            observer.disconnect();
            load();
        }, { rootMargin: '240px 0px', threshold: 0 });

        observer.observe(image);
    });
}

function initCmsEditorComponents(root = document) {
    normalizeCmsParallaxImages(root);
    initCmsLightboxes(root);
    initCmsGalleryCarousels(root);
    initCmsParallax(root);
    initCmsCuriosities(root);
    initLegacyYoutubeEmbeds(root);
    initDeferredImages(root);
}

document.addEventListener('DOMContentLoaded', () => initCmsEditorComponents());
document.addEventListener('livewire:navigated', () => initCmsEditorComponents());
queueMicrotask(() => initCmsEditorComponents());



function normalizeMapGeometry(geometry = {}, options = {}) {
    const center = geometry.center || {};
    const defaultLocale = normalizeLocale(options.defaultLocale || 'it');
    const displayLocale = options.displayLocale ? normalizeLocale(options.displayLocale) : null;
    const locales = normalizeLocales(options.locales || [], defaultLocale);

    return {
        center: {
            lat: Number(center.lat ?? 45.4642),
            lng: Number(center.lng ?? 9.19),
        },
        zoom: Math.min(20, Math.max(1, Number(geometry.zoom ?? 8))),
        markers: Array.isArray(geometry.markers) ? geometry.markers.map((marker) => {
            const fallback = {
                title: String(marker.title || 'Marker'),
                description: String(marker.description || ''),
            };
            const translations = normalizeMapItemTranslations(marker, fallback, ['title', 'description'], locales, defaultLocale);

            return {
                id: String(marker.id || createMapId()),
                title: localizedMapItemValue(translations, 'title', fallback.title, displayLocale, defaultLocale),
                description: localizedMapItemValue(translations, 'description', fallback.description, displayLocale, defaultLocale),
                translations,
                lat: Number(marker.lat || 0),
                lng: Number(marker.lng || 0),
                icon_type: ['emoji', 'lucide'].includes(marker.icon_type) ? marker.icon_type : 'emoji',
                icon: String(marker.icon || '📍'),
                gallery_id: marker.gallery_id ? Number(marker.gallery_id) : null,
                media_id: marker.media_id ? Number(marker.media_id) : null,
                photo: marker.photo || null,
            };
        }) : [],
        paths: Array.isArray(geometry.paths) ? geometry.paths.map((path) => {
            const fallback = {
                title: String(path.title || 'Percorso'),
                description: String(path.description || ''),
            };
            const translations = normalizeMapItemTranslations(path, fallback, ['title', 'description'], locales, defaultLocale);

            return {
                id: String(path.id || createMapId()),
                title: localizedMapItemValue(translations, 'title', fallback.title, displayLocale, defaultLocale),
                description: localizedMapItemValue(translations, 'description', fallback.description, displayLocale, defaultLocale),
                translations,
                color: /^#[0-9a-f]{6}$/i.test(path.color || '') ? path.color : '#2563eb',
                weight: Math.min(12, Math.max(1, Number(path.weight || 4))),
                dash: ['solid', 'dash', 'dot'].includes(path.dash) ? path.dash : 'solid',
                transport: ['walk', 'car', 'plane', 'boat', 'train', 'bike'].includes(path.transport) ? path.transport : 'car',
                distance_km: Number(path.distance_km || 0),
                coordinates: Array.isArray(path.coordinates) ? path.coordinates.map((point) => ({
                    lat: Number(point.lat || 0),
                    lng: Number(point.lng || 0),
                })) : [],
            };
        }).map((path) => ({
            ...path,
            distance_km: Number(path.distance_km || pathDistanceKm(path.coordinates)),
        })) : [],
        shapes: Array.isArray(geometry.shapes) ? geometry.shapes.map((shape) => {
            const fallback = {
                title: String(shape.title || 'Geometria'),
                description: String(shape.description || ''),
                text: String(shape.text || ''),
            };
            const translations = normalizeMapItemTranslations(shape, fallback, ['title', 'description', 'text'], locales, defaultLocale);

            return {
                id: String(shape.id || createMapId()),
                type: ['polygon', 'rectangle', 'circle', 'circle_marker', 'text'].includes(shape.type) ? shape.type : 'polygon',
                title: localizedMapItemValue(translations, 'title', fallback.title, displayLocale, defaultLocale),
                description: localizedMapItemValue(translations, 'description', fallback.description, displayLocale, defaultLocale),
                translations,
                color: /^#[0-9a-f]{6}$/i.test(shape.color || '') ? shape.color : '#0f766e',
                fillColor: /^#[0-9a-f]{6}$/i.test(shape.fillColor || '') ? shape.fillColor : '#14b8a6',
                center: {
                    lat: Number(shape.center?.lat || 0),
                    lng: Number(shape.center?.lng || 0),
                },
                radius: Number(shape.radius || 0),
                text: localizedMapItemValue(translations, 'text', fallback.text, displayLocale, defaultLocale),
                coordinates: Array.isArray(shape.coordinates) ? shape.coordinates.map((point) => ({
                    lat: Number(point.lat || 0),
                    lng: Number(point.lng || 0),
                })) : [],
            };
        }) : [],
    };
}

function normalizeLocale(locale) {
    return String(locale || 'it').split('-')[0].toLowerCase();
}

function mapUiTranslations(locale = document.documentElement.lang || 'it') {
    const translations = {
        it: { map: 'Mappa', marker: 'marker', paths: 'percorsi', geometries: 'geometrie', storyHint: 'La mappa segue il racconto', enable3d: 'Attiva vista 3D', disable3d: 'Disattiva vista 3D', walk: 'A piedi', car: 'Auto', plane: 'Aereo', boat: 'Nave', train: 'Treno', bike: 'Bici', route: 'Percorso' },
        en: { map: 'Map', marker: 'markers', paths: 'paths', geometries: 'geometries', storyHint: 'The map follows the story', enable3d: 'Enable 3D view', disable3d: 'Disable 3D view', walk: 'On foot', car: 'Car', plane: 'Plane', boat: 'Boat', train: 'Train', bike: 'Bike', route: 'Route' },
        fr: { map: 'Carte', marker: 'repères', paths: 'parcours', geometries: 'géométries', storyHint: 'La carte suit le récit', enable3d: 'Activer la vue 3D', disable3d: 'Désactiver la vue 3D', walk: 'À pied', car: 'Voiture', plane: 'Avion', boat: 'Bateau', train: 'Train', bike: 'Vélo', route: 'Parcours' },
        de: { map: 'Karte', marker: 'Marker', paths: 'Routen', geometries: 'Geometrien', storyHint: 'Die Karte folgt der Geschichte', enable3d: '3D-Ansicht aktivieren', disable3d: '3D-Ansicht deaktivieren', walk: 'Zu Fuß', car: 'Auto', plane: 'Flugzeug', boat: 'Boot', train: 'Zug', bike: 'Fahrrad', route: 'Route' },
        es: { map: 'Mapa', marker: 'marcadores', paths: 'rutas', geometries: 'geometrías', storyHint: 'El mapa sigue el relato', enable3d: 'Activar vista 3D', disable3d: 'Desactivar vista 3D', walk: 'A pie', car: 'Coche', plane: 'Avión', boat: 'Barco', train: 'Tren', bike: 'Bici', route: 'Ruta' },
        pl: { map: 'Mapa', marker: 'znaczniki', paths: 'trasy', geometries: 'geometrie', storyHint: 'Mapa podąża za opowieścią', enable3d: 'Włącz widok 3D', disable3d: 'Wyłącz widok 3D', walk: 'Pieszo', car: 'Auto', plane: 'Samolot', boat: 'Łódź', train: 'Pociąg', bike: 'Rower', route: 'Trasa' },
        ru: { map: 'Карта', marker: 'маркеры', paths: 'маршруты', geometries: 'геометрии', storyHint: 'Карта следует за рассказом', enable3d: 'Включить 3D-вид', disable3d: 'Отключить 3D-вид', walk: 'Пешком', car: 'Авто', plane: 'Самолёт', boat: 'Лодка', train: 'Поезд', bike: 'Велосипед', route: 'Маршрут' },
        pt: { map: 'Mapa', marker: 'marcadores', paths: 'percursos', geometries: 'geometrias', storyHint: 'O mapa acompanha o relato', enable3d: 'Ativar vista 3D', disable3d: 'Desativar vista 3D', walk: 'A pé', car: 'Carro', plane: 'Avião', boat: 'Barco', train: 'Comboio', bike: 'Bicicleta', route: 'Percurso' },
        nl: { map: 'Kaart', marker: 'markeringen', paths: 'routes', geometries: 'geometrieën', storyHint: 'De kaart volgt het verhaal', enable3d: '3D-weergave inschakelen', disable3d: '3D-weergave uitschakelen', walk: 'Te voet', car: 'Auto', plane: 'Vliegtuig', boat: 'Boot', train: 'Trein', bike: 'Fiets', route: 'Route' },
        sv: { map: 'Karta', marker: 'markörer', paths: 'rutter', geometries: 'geometrier', storyHint: 'Kartan följer berättelsen', enable3d: 'Aktivera 3D-vy', disable3d: 'Inaktivera 3D-vy', walk: 'Till fots', car: 'Bil', plane: 'Flyg', boat: 'Båt', train: 'Tåg', bike: 'Cykel', route: 'Rutt' },
        fi: { map: 'Kartta', marker: 'merkinnät', paths: 'reitit', geometries: 'geometriat', storyHint: 'Kartta seuraa tarinaa', enable3d: 'Ota 3D-näkymä käyttöön', disable3d: 'Poista 3D-näkymä käytöstä', walk: 'Kävellen', car: 'Auto', plane: 'Lentokone', boat: 'Vene', train: 'Juna', bike: 'Pyörä', route: 'Reitti' },
        da: { map: 'Kort', marker: 'markører', paths: 'ruter', geometries: 'geometrier', storyHint: 'Kortet følger fortællingen', enable3d: 'Aktivér 3D-visning', disable3d: 'Deaktivér 3D-visning', walk: 'Til fods', car: 'Bil', plane: 'Fly', boat: 'Båd', train: 'Tog', bike: 'Cykel', route: 'Rute' },
        nb: { map: 'Kart', marker: 'markører', paths: 'ruter', geometries: 'geometrier', storyHint: 'Kartet følger fortellingen', enable3d: 'Aktiver 3D-visning', disable3d: 'Deaktiver 3D-visning', walk: 'Til fots', car: 'Bil', plane: 'Fly', boat: 'Båt', train: 'Tog', bike: 'Sykkel', route: 'Rute' },
        ko: { map: '지도', marker: '마커', paths: '경로', geometries: '도형', storyHint: '지도가 이야기를 따라갑니다', enable3d: '3D 보기 켜기', disable3d: '3D 보기 끄기', walk: '도보', car: '자동차', plane: '비행기', boat: '배', train: '기차', bike: '자전거', route: '경로' },
        zh: { map: '地图', marker: '标记', paths: '路线', geometries: '几何图形', storyHint: '地图跟随故事展开', enable3d: '开启 3D 视图', disable3d: '关闭 3D 视图', walk: '步行', car: '汽车', plane: '飞机', boat: '船', train: '火车', bike: '自行车', route: '路线' },
        ja: { map: '地図', marker: 'マーカー', paths: 'ルート', geometries: '図形', storyHint: '地図が物語に沿って進みます', enable3d: '3D 表示を有効化', disable3d: '3D 表示を無効化', walk: '徒歩', car: '車', plane: '飛行機', boat: '船', train: '列車', bike: '自転車', route: 'ルート' },
        ar: { map: 'خريطة', marker: 'علامات', paths: 'مسارات', geometries: 'أشكال', storyHint: 'الخريطة تتبع القصة', enable3d: 'تفعيل عرض 3D', disable3d: 'إيقاف عرض 3D', walk: 'سيراً', car: 'سيارة', plane: 'طائرة', boat: 'قارب', train: 'قطار', bike: 'دراجة', route: 'مسار' },
    };

    return { ...translations.en, ...(translations[normalizeLocale(locale)] || {}) };
}

function normalizeLocales(locales = [], defaultLocale = 'it') {
    return [...new Set([normalizeLocale(defaultLocale), ...locales.map((locale) => normalizeLocale(locale)).filter(Boolean)])];
}

function defaultMapItemTranslations(locales, defaultLocale, fallback, fields = ['title', 'description']) {
    return normalizeMapItemTranslations({ translations: {} }, fallback, fields, normalizeLocales(locales, defaultLocale), normalizeLocale(defaultLocale));
}

function normalizeMapItemTranslations(item, fallback, fields, locales, defaultLocale) {
    const source = item.translations && typeof item.translations === 'object' ? item.translations : {};
    const allLocales = normalizeLocales([...locales, ...Object.keys(source)], defaultLocale);

    return allLocales.reduce((translations, locale) => {
        const current = source[locale] || {};
        translations[locale] = fields.reduce((values, field) => ({
            ...values,
            [field]: String(current[field] ?? (locale === defaultLocale ? fallback[field] ?? '' : '')),
        }), {});

        return translations;
    }, {});
}

function localizedMapItemValue(translations, field, fallback, displayLocale, defaultLocale) {
    if (displayLocale && translations?.[displayLocale]?.[field]) {
        return translations[displayLocale][field];
    }

    return translations?.[defaultLocale]?.[field] || fallback || '';
}

let pmtilesProtocolRegistered = false;
let protomapsModulesPromise = null;

function browserSupportsWebGl() {
    const canvas = document.createElement('canvas');

    return Boolean(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
}

function loadProtomapsModules() {
    if (!protomapsModulesPromise) {
        protomapsModulesPromise = Promise.all([
            import('maplibre-gl'),
            import('maplibre-gl/dist/maplibre-gl.css'),
            import('pmtiles'),
            import('@protomaps/basemaps'),
        ]).then(([maplibreModule, _maplibreStyles, pmtilesModule, basemapsModule]) => ({
            maplibregl: maplibreModule.default,
            Protocol: pmtilesModule.Protocol,
            protomapsLayers: basemapsModule.layers,
            namedFlavor: basemapsModule.namedFlavor,
        }));
    }

    return protomapsModulesPromise;
}

function publicProtomapsConfig() {
    return window.NorthwestAlpsMaps?.protomaps || {};
}

function publicProtomapsUrl() {
    const url = String(publicProtomapsConfig().pmtiles_url || '').trim();

    if (!url) {
        return '';
    }

    return url.startsWith('pmtiles://') ? url : `pmtiles://${url}`;
}

function shouldUseProtomaps() {
    return Boolean(publicProtomapsUrl() && browserSupportsWebGl());
}

function registerPmtilesProtocol(maplibregl, Protocol) {
    if (pmtilesProtocolRegistered) {
        return;
    }

    const protocol = new Protocol();
    maplibregl.addProtocol('pmtiles', protocol.tile);
    pmtilesProtocolRegistered = true;
}

function northwestProtomapsFlavor(namedFlavor) {
    const isDark = document.documentElement.classList.contains('dark');
    const base = namedFlavor(isDark ? 'dark' : 'light');

    return {
        ...base,
        background: isDark ? '#2b211d' : '#f7f1e6',
        earth: isDark ? '#2f2520' : '#f7f1e6',
        park_a: isDark ? '#33402f' : '#d8e0c7',
        park_b: isDark ? '#41563a' : '#b9d0a7',
        wood_a: isDark ? '#2e3a2d' : '#cfdec6',
        wood_b: isDark ? '#3d5438' : '#a9ca96',
        scrub_a: isDark ? '#373629' : '#e3dcbc',
        scrub_b: isDark ? '#4b4a32' : '#cec186',
        sand: isDark ? '#3d3429' : '#eadfbe',
        beach: isDark ? '#443a2d' : '#f0e0b6',
        glacier: isDark ? '#354247' : '#e8eef0',
        water: isDark ? '#25454a' : '#a9cfd1',
        buildings: isDark ? '#443833' : '#ded1c4',
        industrial: isDark ? '#3c3634' : '#e3d7cd',
        pedestrian: isDark ? '#3a302a' : '#efe4d0',
        other: isDark ? '#342a25' : '#f2eadf',
        minor_service: isDark ? '#473832' : '#f4eadc',
        minor_a: isDark ? '#4a3b35' : '#f1e6d8',
        minor_b: isDark ? '#534139' : '#fff7ee',
        link: isDark ? '#554139' : '#fff7ee',
        major: isDark ? '#695044' : '#fff3df',
        highway: isDark ? '#7b5a45' : '#f4c892',
        railway: isDark ? '#76675f' : '#9a8d83',
        boundaries: isDark ? '#695c54' : '#b9a99a',
        roads_label_minor: isDark ? '#c4b5a8' : '#7f6b5f',
        roads_label_minor_halo: isDark ? '#2b211d' : '#f7f1e6',
        roads_label_major: isDark ? '#dcc6b5' : '#5f493f',
        roads_label_major_halo: isDark ? '#2b211d' : '#f7f1e6',
        ocean_label: isDark ? '#9fcbd0' : '#547f86',
        subplace_label: isDark ? '#baa99d' : '#816f64',
        subplace_label_halo: isDark ? '#2b211d' : '#f7f1e6',
        city_label: isDark ? '#f2e5d8' : '#46352e',
        city_label_halo: isDark ? '#2b211d' : '#f7f1e6',
        state_label: isDark ? '#bca799' : '#a06e50',
        state_label_halo: isDark ? '#2b211d' : '#f7f1e6',
        country_label: isDark ? '#e0c3ae' : '#8f4128',
        address_label: isDark ? '#c9b8aa' : '#7f6b5f',
        address_label_halo: isDark ? '#2b211d' : '#f7f1e6',
    };
}

function protomapsStyle(modules, locale = 'it') {
    const config = publicProtomapsConfig();
    const isDark = document.documentElement.classList.contains('dark');

    return {
        version: 8,
        glyphs: config.glyphs_url || 'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf',
        sprite: isDark
            ? (config.sprite_dark_url || 'https://protomaps.github.io/basemaps-assets/sprites/v4/dark')
            : (config.sprite_light_url || 'https://protomaps.github.io/basemaps-assets/sprites/v4/light'),
        sources: {
            protomaps: {
                type: 'vector',
                url: publicProtomapsUrl(),
                attribution: config.attribution || '<a href="https://protomaps.com">Protomaps</a> © <a href="https://openstreetmap.org">OpenStreetMap</a>',
            },
        },
        layers: modules.protomapsLayers('protomaps', northwestProtomapsFlavor(modules.namedFlavor), { lang: normalizeLocale(locale || 'it') }),
    };
}

function createCmsPublicMap(container, geometry, options = {}) {
    if (shouldUseProtomaps()) {
        return new MapLibrePublicMapAdapter(container, geometry, options);
    }

    return new LeafletPublicMapAdapter(container, geometry, options);
}

class LeafletPublicMapAdapter {
    constructor(container, geometry, options = {}) {
        this.type = 'leaflet';
        this.map = L.map(container, { scrollWheelZoom: Boolean(options.scrollWheelZoom) })
            .setView([geometry.center.lat, geometry.center.lng], geometry.zoom);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors',
            maxZoom: 19,
        }).addTo(this.map);
        enhanceLeafletMapUi(this.map);
    }

    addShape(shape, isSelected = false) {
        const layer = mapShapeLayer(shape, isSelected);

        if (!layer) {
            return null;
        }

        layer.bindPopup(mapPopupHtml(shape));
        layer.addTo(this.map);

        return layer;
    }

    addPath(path, isSelected = false) {
        const layer = L.polyline(path.coordinates.map((point) => [point.lat, point.lng]), {
            color: path.color,
            weight: Number(path.weight || 4),
            opacity: isSelected ? 0.95 : 0.8,
            dashArray: pathDashArray(path.dash),
        }).bindPopup(mapPopupHtml(path));

        layer.addTo(this.map);

        return layer;
    }

    addMarker(marker, isSelected = false) {
        const layer = L.marker([marker.lat, marker.lng], {
            icon: mapMarkerIcon(marker, isSelected),
        }).bindPopup(mapPopupHtml(marker, marker.photo || null));

        layer.addTo(this.map);

        return layer;
    }

    focusLayer(layer) {
        if (!layer) {
            return;
        }

        if (layer.getLatLng) {
            this.map.setView(layer.getLatLng(), Math.max(this.map.getZoom(), 13), { animate: false });
            layer.openPopup?.();
            return;
        }

        if (layer.getBounds) {
            this.map.fitBounds(layer.getBounds(), { padding: [28, 28], maxZoom: 14, animate: false });
            layer.openPopup?.();
        }
    }

    setMarkerActive(layer, marker, isSelected) {
        layer?.setIcon?.(mapMarkerIcon(marker, isSelected));
    }

    invalidateSize() {
        this.map.invalidateSize();
    }

    remove() {
        this.map.remove();
    }
}

class MapLibrePublicMapAdapter {
    constructor(container, geometry, options = {}) {
        this.type = 'maplibre';
        this.prefix = `cms-map-${Math.random().toString(36).slice(2)}`;
        this.layerIndex = 0;
        this.locale = options.locale || document.documentElement.lang || 'it';
        this.map = null;
        this.maplibregl = null;
        this.ready = this.initialize(container, geometry, options);
    }

    async initialize(container, geometry, options = {}) {
        const modules = await loadProtomapsModules();
        this.maplibregl = modules.maplibregl;
        registerPmtilesProtocol(modules.maplibregl, modules.Protocol);

        this.map = new modules.maplibregl.Map({
            container,
            style: protomapsStyle(modules, this.locale),
            center: [geometry.center.lng, geometry.center.lat],
            zoom: geometry.zoom,
            bearing: 0,
            pitch: 0,
            maxPitch: 60,
            attributionControl: false,
            scrollZoom: Boolean(options.scrollWheelZoom),
            dragRotate: true,
            pitchWithRotate: true,
            touchPitch: true,
        });
        const zoomControl = new modules.maplibregl.NavigationControl({ showCompass: false, showZoom: true });
        const compassControl = new modules.maplibregl.NavigationControl({ showCompass: true, showZoom: false, visualizePitch: true });
        this.map.addControl(zoomControl, 'top-left');
        this.enhanceNavigationControl(zoomControl);
        this.map.addControl(compassControl, 'top-left');
        this.enhanceNavigationControl(compassControl);
        this.map.addControl(this.createViewControl(), 'top-left');
        this.map.addControl(new modules.maplibregl.AttributionControl({ compact: true }), 'bottom-right');

        await new Promise((resolve) => {
            this.map.on('load', resolve);
        });
    }

    enhanceNavigationControl(navigationControl) {
        const zoomInIcon = navigationControl?._zoomInButton?.querySelector('.maplibregl-ctrl-icon');
        const zoomOutIcon = navigationControl?._zoomOutButton?.querySelector('.maplibregl-ctrl-icon');
        const compassIcon = navigationControl?._compassIcon;

        if (zoomInIcon) {
            zoomInIcon.classList.add('cms-map-zoom-icon');
            zoomInIcon.innerHTML = lucideControlIcon('plus');
        }

        if (zoomOutIcon) {
            zoomOutIcon.classList.add('cms-map-zoom-icon');
            zoomOutIcon.innerHTML = lucideControlIcon('minus');
        }

        if (compassIcon) {
            compassIcon.classList.add('cms-map-compass-icon');
            compassIcon.innerHTML = lucideControlIcon('compass');
        }
    }

    createViewControl() {
        let map = null;
        let container = null;
        let button = null;
        let isTilted = false;
        const ui = mapUiTranslations(this.locale);

        const updateButton = () => {
            if (!button) {
                return;
            }

            button.classList.toggle('is-active', isTilted);
            button.setAttribute('aria-pressed', String(isTilted));
            button.setAttribute('aria-label', isTilted ? ui.disable3d : ui.enable3d);
            button.setAttribute('title', isTilted ? ui.disable3d : ui.enable3d);
        };

        return {
            onAdd: (mapInstance) => {
                map = mapInstance;
                container = document.createElement('div');
                container.className = 'maplibregl-ctrl maplibregl-ctrl-group cms-map-view-control';

                button = document.createElement('button');
                button.type = 'button';
                button.className = 'cms-map-3d-toggle';
                button.innerHTML = `
                    <span class="cms-map-3d-icon" aria-hidden="true">
                        ${lucideControlIcon('box')}
                    </span>
                `;
                button.addEventListener('click', () => {
                    isTilted = !isTilted;
                    map.easeTo({
                        pitch: isTilted ? 55 : 0,
                        bearing: isTilted ? -22 : 0,
                        duration: 420,
                    });
                    updateButton();
                });

                container.appendChild(button);
                updateButton();

                return container;
            },
            onRemove: () => {
                container?.remove();
                map = null;
                container = null;
                button = null;
            },
        };
    }

    nextId(kind, id) {
        this.layerIndex += 1;

        return `${this.prefix}-${kind}-${String(id || this.layerIndex).replace(/[^a-zA-Z0-9_-]/g, '-')}-${this.layerIndex}`;
    }

    addShape(shape, isSelected = false) {
        if (shape.type === 'text') {
            const element = document.createElement('div');
            element.innerHTML = `<div class="cms-map-text-marker">${escapeHtml(shape.text || shape.title || 'Testo')}</div>`;
            const layer = {
                type: 'marker',
                marker: null,
                element,
                center: [shape.center.lng, shape.center.lat],
            };

            this.ready.then(() => {
                layer.marker = new this.maplibregl.Marker({ element, anchor: 'center' })
                    .setLngLat(layer.center)
                    .setPopup(new this.maplibregl.Popup({ offset: 18, focusAfterOpen: false }).setHTML(mapPopupHtml(shape)))
                    .addTo(this.map);
            });

            return layer;
        }

        const id = this.nextId('shape', shape.id);
        const isCircle = shape.type === 'circle';
        const isCircleMarker = shape.type === 'circle_marker';

        if (!isCircle && !isCircleMarker && (!Array.isArray(shape.coordinates) || shape.coordinates.length < 3)) {
            return null;
        }

        const coordinates = isCircle
            ? [circlePolygonCoordinates(shape.center, Number(shape.radius || 250))]
            : isCircleMarker
                ? [shape.center.lng, shape.center.lat]
                : [closedLngLatRing(shape.coordinates)];
        const geojson = {
            type: 'FeatureCollection',
            features: [{
                type: 'Feature',
                properties: {},
                geometry: {
                    type: isCircleMarker ? 'Point' : 'Polygon',
                    coordinates,
                },
            }],
        };
        const bounds = isCircleMarker
            ? [[shape.center.lng, shape.center.lat], [shape.center.lng, shape.center.lat]]
            : lngLatBounds(isCircle ? coordinates[0] : coordinates[0]);
        const center = Number.isFinite(Number(shape.center?.lng)) && Number.isFinite(Number(shape.center?.lat))
            ? [shape.center.lng, shape.center.lat]
            : boundsCenter(bounds);
        const layer = {
            type: 'maplibre-layer',
            id,
            center,
            bounds,
            popupHtml: mapPopupHtml(shape),
            popup: null,
        };

        this.ready.then(() => {
            this.map.addSource(id, { type: 'geojson', data: geojson });
            this.map.addLayer({
                id,
                type: isCircleMarker ? 'circle' : 'fill',
                source: id,
                paint: isCircleMarker
                    ? {
                        'circle-color': shape.fillColor || '#14b8a6',
                        'circle-radius': Math.max(6, Number(shape.radius || 10)),
                        'circle-stroke-color': shape.color || '#0f766e',
                        'circle-stroke-width': isSelected ? 4 : 2,
                        'circle-opacity': 0.86,
                    }
                    : {
                        'fill-color': shape.fillColor || '#14b8a6',
                        'fill-opacity': 0.22,
                        'fill-outline-color': shape.color || '#0f766e',
                    },
            });
            this.map.on('click', id, (event) => this.openLayerPopup(layer, event.lngLat));
            this.map.on('mouseenter', id, () => { this.map.getCanvas().style.cursor = 'pointer'; });
            this.map.on('mouseleave', id, () => { this.map.getCanvas().style.cursor = ''; });
        });

        return layer;
    }

    addPath(path, isSelected = false) {
        if (!Array.isArray(path.coordinates) || path.coordinates.length < 2) {
            return null;
        }

        const id = this.nextId('path', path.id);
        const coordinates = path.coordinates.map((point) => [point.lng, point.lat]);
        const bounds = lngLatBounds(coordinates);
        const layer = {
            type: 'maplibre-layer',
            id,
            center: boundsCenter(bounds),
            bounds,
            popupHtml: mapPopupHtml(path),
            popup: null,
        };

        this.ready.then(() => {
            this.map.addSource(id, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [{
                        type: 'Feature',
                        properties: {},
                        geometry: { type: 'LineString', coordinates },
                    }],
                },
            });
            this.map.addLayer({
                id,
                type: 'line',
                source: id,
                layout: {
                    'line-cap': 'round',
                    'line-join': 'round',
                },
                paint: {
                    'line-color': path.color || '#8f4128',
                    'line-width': Number(path.weight || 4) + (isSelected ? 1 : 0),
                    'line-opacity': isSelected ? 0.95 : 0.8,
                    ...(mapLibreDashArray(path.dash) ? { 'line-dasharray': mapLibreDashArray(path.dash) } : {}),
                },
            });
            this.map.on('click', id, (event) => this.openLayerPopup(layer, event.lngLat));
            this.map.on('mouseenter', id, () => { this.map.getCanvas().style.cursor = 'pointer'; });
            this.map.on('mouseleave', id, () => { this.map.getCanvas().style.cursor = ''; });
        });

        return layer;
    }

    addMarker(marker, isSelected = false) {
        const element = document.createElement('div');
        element.innerHTML = mapMarkerHtml(marker, isSelected);
        const layer = {
            type: 'marker',
            marker: null,
            element,
            center: [marker.lng, marker.lat],
        };

        this.ready.then(() => {
            layer.marker = new this.maplibregl.Marker({ element, anchor: 'bottom' })
                .setLngLat(layer.center)
                .setPopup(new this.maplibregl.Popup({ offset: [0, -34], focusAfterOpen: false }).setHTML(mapPopupHtml(marker, marker.photo || null)))
                .addTo(this.map);
        });

        return layer;
    }

    openLayerPopup(layer, lngLat = null) {
        const target = lngLat || layer.center;
        layer.popup ||= new this.maplibregl.Popup({ offset: 14, focusAfterOpen: false }).setHTML(layer.popupHtml || '');
        layer.popup.setLngLat(target).addTo(this.map);
    }

    focusLayer(layer) {
        if (!layer) {
            return;
        }

        this.ready.then(() => {
            if (layer.type === 'marker') {
                this.map.jumpTo({ center: layer.center, zoom: Math.max(this.map.getZoom(), 13) });
                layer.marker?.getPopup()?.setLngLat(layer.center).addTo(this.map);
                return;
            }

            if (layer.bounds) {
                this.map.fitBounds(layer.bounds, { padding: 28, maxZoom: 14, duration: 0 });
                this.openLayerPopup(layer);
            }
        });
    }

    setMarkerActive(layer, marker, isSelected) {
        if (layer?.element) {
            layer.element.innerHTML = mapMarkerHtml(marker, isSelected);
        }
    }

    invalidateSize() {
        if (this.map) {
            this.map.resize();
            return;
        }

        this.ready.then(() => this.map?.resize());
    }

    remove() {
        if (this.map) {
            this.map.remove();
            return;
        }

        this.ready.then(() => this.map?.remove());
    }
}

function mapShapeLayer(shape, isSelected = false) {
    const style = {
        color: shape.color || '#0f766e',
        fillColor: shape.fillColor || '#14b8a6',
        fillOpacity: shape.type === 'circle_marker' ? 0.8 : 0.22,
        weight: isSelected ? 5 : 3,
        opacity: isSelected ? 0.95 : 0.8,
    };

    if (shape.type === 'circle') {
        return L.circle([shape.center.lat, shape.center.lng], {
            ...style,
            radius: Number(shape.radius || 250),
        });
    }

    if (shape.type === 'circle_marker') {
        return L.circleMarker([shape.center.lat, shape.center.lng], {
            ...style,
            radius: Math.max(6, Number(shape.radius || 10)),
        });
    }

    if (shape.type === 'text') {
        return L.marker([shape.center.lat, shape.center.lng], {
            icon: L.divIcon({
                className: '',
                html: `<div class="cms-map-text-marker">${escapeHtml(shape.text || shape.title || 'Testo')}</div>`,
            }),
        });
    }

    if (shape.coordinates.length < 3) {
        return null;
    }

    return L.polygon(shape.coordinates.map((point) => [point.lat, point.lng]), style);
}

function shapeBounds(shape) {
    if (['circle', 'circle_marker', 'text'].includes(shape.type)) {
        return shape.center?.lat && shape.center?.lng
            ? L.latLngBounds([[shape.center.lat, shape.center.lng]])
            : null;
    }

    if (!shape.coordinates?.length) {
        return null;
    }

    return L.latLngBounds(shape.coordinates.map((point) => [point.lat, point.lng]));
}

function mapMarkerHtml(marker, isSelected = false) {
    const content = marker.icon_type === 'lucide'
        ? lucideMarkerSvg(marker.icon)
        : `<span class="text-lg leading-none">${escapeHtml(marker.icon || '📍')}</span>`;

    return `
        <div class="cms-map-marker ${isSelected ? 'is-selected' : ''}">
            ${content}
        </div>
    `;
}

function mapMarkerIcon(marker, isSelected = false) {
    return L.divIcon({
        className: '',
        html: mapMarkerHtml(marker, isSelected),
        iconSize: [40, 40],
        iconAnchor: [20, 40],
        popupAnchor: [0, -36],
    });
}

function circlePolygonCoordinates(center, radiusMeters, steps = 64) {
    const coordinates = [];
    const lat = Number(center.lat);
    const lng = Number(center.lng);
    const earthRadius = 6378137;
    const angularDistance = radiusMeters / earthRadius;
    const latRadians = lat * Math.PI / 180;
    const lngRadians = lng * Math.PI / 180;

    for (let index = 0; index <= steps; index += 1) {
        const bearing = 2 * Math.PI * index / steps;
        const pointLat = Math.asin(
            Math.sin(latRadians) * Math.cos(angularDistance)
            + Math.cos(latRadians) * Math.sin(angularDistance) * Math.cos(bearing),
        );
        const pointLng = lngRadians + Math.atan2(
            Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latRadians),
            Math.cos(angularDistance) - Math.sin(latRadians) * Math.sin(pointLat),
        );

        coordinates.push([pointLng * 180 / Math.PI, pointLat * 180 / Math.PI]);
    }

    return coordinates;
}

function closedLngLatRing(points = []) {
    const coordinates = points.map((point) => [Number(point.lng), Number(point.lat)]);
    const first = coordinates[0];
    const last = coordinates[coordinates.length - 1];

    if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
        coordinates.push([...first]);
    }

    return coordinates;
}

function lngLatBounds(coordinates = []) {
    const valid = coordinates.filter((coordinate) => Number.isFinite(coordinate[0]) && Number.isFinite(coordinate[1]));

    if (valid.length === 0) {
        return [[0, 0], [0, 0]];
    }

    return valid.reduce((bounds, coordinate) => [
        [Math.min(bounds[0][0], coordinate[0]), Math.min(bounds[0][1], coordinate[1])],
        [Math.max(bounds[1][0], coordinate[0]), Math.max(bounds[1][1], coordinate[1])],
    ], [[valid[0][0], valid[0][1]], [valid[0][0], valid[0][1]]]);
}

function boundsCenter(bounds) {
    return [
        (bounds[0][0] + bounds[1][0]) / 2,
        (bounds[0][1] + bounds[1][1]) / 2,
    ];
}

function mapLibreDashArray(dash) {
    return {
        dash: [2.5, 2],
        dot: [0.2, 2],
        solid: null,
    }[dash] || null;
}

function lucideMarkerSvg(name, className = 'size-5') {
    const path = lucideIcons[String(name || '').toLowerCase()] || lucideIcons['map-pin'];

    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="${className}">${path}</svg>`;
}

function enhanceLeafletMapUi(map) {
    const container = map?.getContainer?.();

    if (!container) {
        return;
    }

    requestAnimationFrame(() => {
        replaceControlIcon(container.querySelector('.leaflet-control-zoom-in'), 'plus');
        replaceControlIcon(container.querySelector('.leaflet-control-zoom-out'), 'minus');

        const geomanIcons = {
            'leaflet-pm-icon-marker': 'map-pin',
            'leaflet-pm-icon-polyline': 'route',
            'leaflet-pm-icon-rectangle': 'square',
            'leaflet-pm-icon-polygon': 'pentagon',
            'leaflet-pm-icon-circle': 'circle',
            'leaflet-pm-icon-circle-marker': 'circle-dot',
            'leaflet-pm-icon-text': 'type',
            'leaflet-pm-icon-edit': 'pencil',
            'leaflet-pm-icon-drag': 'move',
            'leaflet-pm-icon-cut': 'scissors',
            'leaflet-pm-icon-delete': 'trash-2',
            'leaflet-pm-icon-rotate': 'rotate-cw',
            'leaflet-pm-icon-snapping': 'magnet',
        };

        Object.entries(geomanIcons).forEach(([className, icon]) => {
            container.querySelectorAll(`.${className}`).forEach((element) => {
                element.classList.add('cms-leaflet-lucide-icon');
                element.innerHTML = lucideControlIcon(icon);
            });
        });
    });
}

function replaceControlIcon(element, icon) {
    if (!element) {
        return;
    }

    element.innerHTML = lucideControlIcon(icon);
    element.classList.add('cms-leaflet-icon-button');
}

function lucideControlIcon(name) {
    const icons = {
        box: '<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>',
        circle: '<circle cx="12" cy="12" r="9"/>',
        'circle-dot': '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2"/>',
        compass: '<circle cx="12" cy="12" r="10"/><path d="m16.24 7.76-2.12 6.36-6.36 2.12 2.12-6.36Z"/>',
        magnet: '<path d="m6 15-4-4 1.5-1.5L5 11V8a7 7 0 0 1 14 0v3l1.5-1.5L22 11l-4 4-4-4 1.5-1.5L17 11V8a5 5 0 0 0-10 0v3l1.5-1.5L10 11Z"/><path d="M5 19h4"/><path d="M15 19h4"/>',
        'map-pin': '<path d="M20 10c0 4.9-5.5 10.3-7.4 12a1 1 0 0 1-1.2 0C9.5 20.3 4 14.9 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/>',
        minus: '<path d="M5 12h14"/>',
        move: '<path d="M12 2v20"/><path d="m15 5-3-3-3 3"/><path d="m15 19-3 3-3-3"/><path d="M2 12h20"/><path d="m5 9-3 3 3 3"/><path d="m19 9 3 3-3 3"/>',
        pencil: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>',
        pentagon: '<path d="M12 3 4 8.5 7 20h10l3-11.5Z"/>',
        play: '<path d="m6 3 14 9-14 9Z"/>',
        plus: '<path d="M5 12h14"/><path d="M12 5v14"/>',
        'rotate-cw': '<path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v6h-6"/>',
        route: '<circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7H6.5a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/>',
        scissors: '<circle cx="6" cy="6" r="3"/><path d="M8.12 8.12 12 12"/><path d="M20 4 8.12 15.88"/><circle cx="6" cy="18" r="3"/><path d="M14.8 14.8 20 20"/>',
        square: '<rect width="18" height="18" x="3" y="3" rx="2"/>',
        trash: '<path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/>',
        'trash-2': '<path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M19 6l-1 14H6L5 6"/>',
        type: '<path d="M4 7V4h16v3"/><path d="M9 20h6"/><path d="M12 4v16"/>',
    };
    const path = icons[name] || icons['map-pin'];

    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
}

function mapPopupHtml(item, photo = null, locale = document.documentElement.lang || 'it') {
    const ui = mapUiTranslations(locale);
    const title = escapeHtml(item.title || ui.map);
    const description = escapeHtml(item.description || '').replace(/\n/g, '<br>');
    const distance = item.distance_km ? `<div class="cms-map-popup-meta">${transportLabel(item.transport, locale)} · ${Number(item.distance_km).toLocaleString(normalizeLocale(locale), { maximumFractionDigits: 2 })} km</div>` : '';
    const photoHtml = photo?.urls?.thumb
        ? `<div class="cms-map-popup-media"><img src="${escapeHtml(photo.urls.thumb)}" alt="${escapeHtml(photo.alt || photo.name || title)}" class="cms-map-popup-photo"></div>`
        : '';

    return `
        <div class="cms-map-popup">
            ${photoHtml}
            <div class="cms-map-popup-body">
                <div class="cms-map-popup-title">${title}</div>
                ${distance}
                ${description ? `<div class="cms-map-popup-description">${description}</div>` : ''}
            </div>
        </div>
    `;
}

function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function createMapId() {
    return window.crypto?.randomUUID?.() || `map-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function roundCoordinate(value) {
    return Number(Number(value).toFixed(7));
}

function pathDashArray(dash) {
    return {
        dash: '10 8',
        dot: '2 8',
        solid: null,
    }[dash] || null;
}

function transportLabel(transport, locale = document.documentElement.lang || 'it') {
    const ui = mapUiTranslations(locale);

    return {
        walk: ui.walk,
        car: ui.car,
        plane: ui.plane,
        boat: ui.boat,
        train: ui.train,
        bike: ui.bike,
    }[transport] || ui.route;
}

function pathDistanceKm(coordinates = []) {
    let meters = 0;

    coordinates.forEach((point, index) => {
        if (index === 0) {
            return;
        }

        meters += haversineMeters(coordinates[index - 1], point);
    });

    return Number((meters / 1000).toFixed(2));
}

function haversineMeters(from, to) {
    const radius = 6371000;
    const fromLat = degreesToRadians(Number(from.lat || 0));
    const toLat = degreesToRadians(Number(to.lat || 0));
    const deltaLat = degreesToRadians(Number(to.lat || 0) - Number(from.lat || 0));
    const deltaLng = degreesToRadians(Number(to.lng || 0) - Number(from.lng || 0));
    const a = Math.sin(deltaLat / 2) ** 2
        + Math.cos(fromLat) * Math.cos(toLat) * Math.sin(deltaLng / 2) ** 2;

    return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function degreesToRadians(value) {
    return value * Math.PI / 180;
}

function flattenLatLngs(latlngs) {
    return latlngs.flatMap((item) => Array.isArray(item) ? flattenLatLngs(item) : [item]);
}

function geomanShapeToCmsShape(shape) {
    return {
        Polygon: 'polygon',
        Rectangle: 'rectangle',
        Circle: 'circle',
        CircleMarker: 'circle_marker',
        Text: 'text',
    }[shape] || 'polygon';
}

function defaultShapeTitle(type) {
    return {
        polygon: 'Nuovo poligono',
        rectangle: 'Nuovo rettangolo',
        circle: 'Nuovo cerchio',
        circle_marker: 'Nuovo circle marker',
        text: 'Nuovo testo',
    }[type] || 'Nuova geometria';
}

function readGeomanText(layer) {
    const element = layer.getElement?.();

    return element?.querySelector?.('textarea')?.value
        || element?.textContent?.trim()
        || 'Testo';
}

function geomanItalianTranslations() {
    return {
        tooltips: {
            placeMarker: 'Clicca per posizionare il marker',
            firstVertex: 'Clicca per inserire il primo punto',
            continueLine: 'Clicca per continuare il disegno',
            finishLine: 'Clicca su un punto o premi Fine per completare la linea',
            finishPoly: 'Clicca sul primo punto o premi Fine per chiudere il poligono',
            finishRect: 'Clicca per completare il rettangolo',
            startCircle: 'Clicca per impostare il centro del cerchio',
            finishCircle: 'Clicca per completare il cerchio',
            placeCircleMarker: 'Clicca per posizionare il circle marker',
            placeText: 'Clicca per inserire il testo',
            selectFirstLayerFor: 'Seleziona il primo elemento per',
            selectSecondLayerFor: 'Seleziona il secondo elemento per',
            'freehand-start': 'Tieni premuto e disegna liberamente',
            'lasso-start': 'Tieni premuto e seleziona con il lasso',
            placeCustomShape: 'Clicca per posizionare la forma',
        },
        actions: {
            finish: 'Fine',
            cancel: 'Annulla',
            removeLastVertex: 'Rimuovi ultimo punto',
            reset: 'Ripristina',
            clear: 'Svuota',
        },
        buttonTitles: {
            drawMarkerButton: 'Disegna marker',
            drawPolyButton: 'Disegna poligono',
            drawLineButton: 'Disegna linea o percorso',
            drawCircleButton: 'Disegna cerchio',
            drawRectButton: 'Disegna rettangolo',
            editButton: 'Modifica elementi',
            dragButton: 'Sposta elementi',
            cutButton: 'Taglia geometria',
            deleteButton: 'Elimina elementi',
            drawCircleMarkerButton: 'Disegna circle marker',
            snappingButton: 'Aggancio',
            pinningButton: 'Fissa vertici condivisi',
            rotateButton: 'Ruota elementi',
            drawTextButton: 'Disegna testo',
            scaleButton: 'Scala elementi',
            autoTracingButton: 'Tracciamento automatico',
            snapGuidesButton: 'Guide di aggancio',
            unionButton: 'Unisci geometrie',
            differenceButton: 'Sottrai geometrie',
            sendToBackButton: 'Porta dietro',
            bringToFrontButton: 'Porta davanti',
            copyLayerButton: 'Copia elemento',
            splitButton: 'Dividi linea',
            lineSimplificationButton: 'Semplifica linea',
            lassoButton: 'Selezione lazo',
            freeHandButton: 'Disegno libero',
            customShapeButton: 'Forma personalizzata',
        },
        measurements: {
            totalLength: 'Lunghezza totale',
            segmentLength: 'Lunghezza segmento',
            area: 'Area',
            radius: 'Raggio',
            perimeter: 'Perimetro',
            height: 'Altezza',
            width: 'Larghezza',
            coordinates: 'Coordinate',
            coordinatesMarker: 'Coordinate marker',
        },
    };
}

document.addEventListener('DOMContentLoaded', () => {
    initializeCmsMapStories();
});
document.addEventListener('livewire:navigated', () => {
    initializeCmsMapStories();
});

let shouldResetTravelDayScroll = false;

function isTravelDayPath(url) {
    try {
        const parsedUrl = url instanceof URL ? url : new URL(String(url), window.location.origin);

        return /^\/[^/]+\/viaggi\/[^/]+\/giorni\/[^/]+\/?$/.test(parsedUrl.pathname);
    } catch (_) {
        return false;
    }
}

document.addEventListener('livewire:navigate', (event) => {
    shouldResetTravelDayScroll = isTravelDayPath(event.detail?.url) && !event.detail?.history;
});

document.addEventListener('livewire:navigated', () => {
    if (!shouldResetTravelDayScroll || window.location.hash || !document.querySelector('[data-travel-day-show]')) {
        shouldResetTravelDayScroll = false;

        return;
    }

    shouldResetTravelDayScroll = false;
    window.requestAnimationFrame(() => window.scrollTo(0, 0));
});

function initializeCmsMapStories() {
    const anchors = Array.from(document.querySelectorAll('.cms-map-embed[data-cms-map-id]'))
        .filter((anchor) => !anchor.closest('.editor-content') && !anchor.closest('[data-travel-day-map-story]') && !['true', 'loading', 'waiting'].includes(anchor.dataset.cmsMapStoryReady));
    const mapIds = [...new Set(anchors.map((anchor) => anchor.dataset.cmsMapId).filter(Boolean))];

    if (mapIds.length === 0) {
        return;
    }

    mapIds.forEach((mapId) => {
        const relatedAnchors = anchors.filter((anchor) => anchor.dataset.cmsMapId === mapId);

        if (relatedAnchors.length === 0) {
            return;
        }

        relatedAnchors.forEach((anchor) => {
            anchor.dataset.cmsMapStoryReady = 'waiting';
        });

        const loadStory = async () => {
            relatedAnchors.forEach((anchor) => {
                anchor.dataset.cmsMapStoryReady = 'loading';
            });

            try {
                await Promise.all([
                    loadLucideCatalog(),
                    shouldUseProtomaps() ? Promise.resolve() : loadLeaflet(),
                ]);

                const locale = normalizeLocale(document.documentElement.lang || 'it');
                const response = await fetch(`/maps/${mapId}/story.json?locale=${encodeURIComponent(locale)}`, { headers: { Accept: 'application/json' } });

                if (!response.ok) {
                    throw new Error(`Unable to load map story ${mapId}`);
                }

                const payload = await response.json();
                createCmsMapStory(payload, relatedAnchors);
            } catch (_) {
                relatedAnchors.forEach((anchor) => {
                    anchor.dataset.cmsMapStoryReady = 'false';
                });
            }
        };

        if (!('IntersectionObserver' in window)) {
            loadStory();

            return;
        }

        const observer = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) {
                return;
            }

            observer.disconnect();
            loadStory();
        }, { rootMargin: '600px 0px', threshold: 0 });

        relatedAnchors.forEach((anchor) => observer.observe(anchor));
    });
}

function createCmsMapStory(payload, anchors) {
    const firstAnchor = anchors[0];
    const isStandalone = firstAnchor.dataset.cmsMapStandalone === 'true';
    const locale = normalizeLocale(payload.locale || document.documentElement.lang || 'it');
    const ui = mapUiTranslations(locale);
    const shell = document.createElement('div');
    const header = document.createElement('div');
    const frame = document.createElement('div');
    const mapElement = document.createElement('div');
    const geometry = normalizeMapGeometry(payload.geometry || {}, {
        defaultLocale: payload.default_locale || 'it',
        displayLocale: payload.locale || document.documentElement.lang || 'it',
    });
    const layers = new Map();

    shell.className = 'cms-map-story-shell';
    shell.classList.toggle('cms-map-story-shell--standalone', isStandalone);
    header.className = 'cms-map-story-header';
    frame.className = 'cms-map-story-frame';
    mapElement.className = 'cms-map-story-map';
    mapElement.setAttribute('aria-label', payload.title || firstAnchor.dataset.cmsMapTitle || ui.map);

    if (! isStandalone) {
        header.innerHTML = `
            <div class="cms-map-story-heading">
                <span class="cms-map-story-kicker">${escapeHtml(payload.title || firstAnchor.dataset.cmsMapTitle || ui.map)}</span>
                <span class="cms-map-story-counts">
                    ${geometry.markers.length} ${ui.marker}
                    ${geometry.paths.length ? ` · ${geometry.paths.length} ${ui.paths}` : ''}
                    ${geometry.shapes.length ? ` · ${geometry.shapes.length} ${ui.geometries}` : ''}
                </span>
            </div>
            <div class="cms-map-story-hint">${escapeHtml(ui.storyHint)}</div>
        `;
        shell.appendChild(header);
    }

    frame.appendChild(mapElement);
    shell.appendChild(frame);
    firstAnchor.before(shell);
    anchors.forEach((anchor) => {
        anchor.dataset.cmsMapStoryReady = 'true';
    });

    if (isStandalone) {
        firstAnchor.hidden = true;
    }

    const map = createCmsPublicMap(mapElement, geometry, {
        scrollWheelZoom: isStandalone,
        locale: payload.locale || document.documentElement.lang || 'it',
    });

    geometry.shapes.forEach((shape) => {
        const layer = map.addShape(shape, false);

        if (layer) {
            layers.set(`shape:${shape.id}`, layer);
        }
    });

    geometry.paths.forEach((path) => {
        const layer = map.addPath(path, false);

        if (layer) {
            layers.set(`path:${path.id}`, layer);
        }
    });

    geometry.markers.forEach((marker) => {
        const layer = map.addMarker(marker, false);
        layers.set(`marker:${marker.id}`, layer);
    });

    shell.__cmsMapStoryApi = {
        focus(type, id) {
            const target = `${type}:${id}`;
            const layer = layers.get(target);

            if (!layer) {
                return;
            }

            geometry.markers.forEach((marker) => {
                const markerLayer = layers.get(`marker:${marker.id}`);
                map.setMarkerActive?.(markerLayer, marker, target === `marker:${marker.id}`);
            });

            map.focusLayer?.(layer);
        },
    };

    setTimeout(() => map.invalidateSize(), 100);

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) {
                return;
            }

            focusCmsMapStoryTarget(map, layers, entry.target);
        });
    }, {
        rootMargin: '-35% 0px -45% 0px',
        threshold: 0.05,
    });

    anchors.forEach((anchor) => observer.observe(anchor));
}

document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-cms-map-focus-marker]');

    if (!trigger) {
        return;
    }

    const markerId = trigger.dataset.cmsMapFocusMarker;
    const storyShell = trigger.closest('article')?.querySelector('.cms-map-story-shell');

    if (!markerId || !storyShell?.__cmsMapStoryApi) {
        return;
    }

    storyShell.__cmsMapStoryApi.focus('marker', markerId);
});

function focusCmsMapStoryTarget(map, layers, anchor) {
    anchor.closest('article')?.querySelectorAll(`.cms-map-embed[data-cms-map-id="${anchor.dataset.cmsMapId}"]`).forEach((item) => {
        item.classList.toggle('is-active', item === anchor);
    });

    const markerId = anchor.dataset.cmsMapMarkerId;
    const pathId = anchor.dataset.cmsMapPathId;
    const shapeId = anchor.dataset.cmsMapShapeId;
    const layer = markerId
        ? layers.get(`marker:${markerId}`)
        : pathId
            ? layers.get(`path:${pathId}`)
            : shapeId
                ? layers.get(`shape:${shapeId}`)
                : null;

    if (!layer) {
        return;
    }

    map.focusLayer?.(layer);
}

Alpine.data('cmsTravelDayMapStory', (config = {}) => ({
    activeTarget: null,
    anchors: [],
    geometry: null,
    locale: config.locale || document.documentElement.lang || 'it',
    mapId: config.mapId,
    mapModalOpen: false,
    maps: new Map(),
    observer: null,
    canvasObserver: null,
    storyLoadPromise: null,
    payload: null,

    init() {
        if (!this.mapId) {
            return;
        }

        this.prepareAnchors();
        const canvas = this.$root.querySelector('[data-map-story-canvas]');

        if (!canvas || !('IntersectionObserver' in window)) {
            this.loadAndRenderCanvas(canvas, 'desktop');

            return;
        }

        this.canvasObserver = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.2)) {
                return;
            }

            this.canvasObserver?.disconnect();
            this.loadAndRenderCanvas(canvas, 'desktop');
        }, { threshold: [0.2] });
        this.canvasObserver.observe(canvas);
    },

    destroy() {
        this.observer?.disconnect();
        this.canvasObserver?.disconnect();
        this.maps.forEach(({ map }) => map.remove());
        this.maps.clear();
        document.documentElement.classList.remove('overflow-hidden');
    },

    async loadStory() {
        const response = await fetch(`/maps/${this.mapId}/story.json?locale=${encodeURIComponent(this.locale)}`, {
            headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
            return;
        }

        this.payload = await response.json();
        this.geometry = normalizeMapGeometry(this.payload.geometry || {}, {
            defaultLocale: this.payload.default_locale || 'it',
            displayLocale: this.payload.locale || this.locale,
        });
    },

    async ensureStoryLoaded() {
        if (!this.storyLoadPromise) {
            this.storyLoadPromise = Promise.all([
                loadLucideCatalog(),
                shouldUseProtomaps() ? Promise.resolve() : loadLeaflet(),
                this.loadStory(),
            ]);
        }

        await this.storyLoadPromise;
    },

    async loadAndRenderCanvas(canvas, key) {
        await this.ensureStoryLoaded();
        this.renderCanvas(canvas, key);

        if (this.anchors.length > 0 && !this.activeTarget) {
            this.focusAnchor(this.anchors[0], false);
        }
    },

    prepareAnchors() {
        this.anchors = Array.from(this.$root.querySelectorAll(`.cms-map-story-content .cms-map-embed[data-cms-map-id="${this.mapId}"]`));

        this.anchors.forEach((anchor) => {
            anchor.dataset.cmsMapStoryReady = 'true';
            anchor.setAttribute('aria-hidden', 'true');
        });

        this.observer?.disconnect();
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    this.focusAnchor(entry.target);
                }
            });
        }, {
            rootMargin: '-35% 0px -45% 0px',
            threshold: 0,
        });

        this.anchors.forEach((anchor) => this.observer.observe(anchor));
    },

    renderCanvas(canvas, key) {
        if (!canvas || !this.geometry || this.maps.has(key)) {
            return;
        }

        const map = createCmsPublicMap(canvas, this.geometry, {
            scrollWheelZoom: false,
            locale: this.locale,
        });
        const layers = new Map();

        this.geometry.shapes.forEach((shape) => {
            const layer = map.addShape(shape, this.activeTarget === `shape:${shape.id}`);

            if (layer) {
                layers.set(`shape:${shape.id}`, layer);
            }
        });

        this.geometry.paths.forEach((path) => {
            const layer = map.addPath(path, this.activeTarget === `path:${path.id}`);

            if (layer) {
                layers.set(`path:${path.id}`, layer);
            }
        });

        this.geometry.markers.forEach((marker) => {
            const layer = map.addMarker(marker, this.activeTarget === `marker:${marker.id}`);
            layers.set(`marker:${marker.id}`, layer);
        });

        this.maps.set(key, { map, layers });
        setTimeout(() => {
            map.invalidateSize();
            this.focusActiveTarget(key, false);
        }, 120);
    },

    focusAnchor(anchor) {
        const markerId = anchor.dataset.cmsMapMarkerId;
        const pathId = anchor.dataset.cmsMapPathId;
        const shapeId = anchor.dataset.cmsMapShapeId;
        const target = markerId
            ? `marker:${markerId}`
            : pathId
                ? `path:${pathId}`
                : shapeId
                    ? `shape:${shapeId}`
                    : null;

        if (!target || target === this.activeTarget) {
            return;
        }

        this.activeTarget = target;
        this.anchors.forEach((item) => item.classList.toggle('is-active', item === anchor));
        this.refreshMarkerIcons();
        this.maps.forEach((_, key) => this.focusActiveTarget(key));
    },

    focusActiveTarget(key) {
        const entry = this.maps.get(key);

        if (!entry || !this.activeTarget) {
            return;
        }

        const layer = entry.layers.get(this.activeTarget);

        if (!layer) {
            return;
        }

        entry.map.focusLayer?.(layer);
    },

    refreshMarkerIcons() {
        this.maps.forEach(({ map, layers }) => {
            this.geometry?.markers.forEach((marker) => {
                const layer = layers.get(`marker:${marker.id}`);
                map.setMarkerActive?.(layer, marker, this.activeTarget === `marker:${marker.id}`);
            });
        });
    },

    async openMapModal() {
        this.mapModalOpen = true;
        document.documentElement.classList.add('overflow-hidden');
        this.$nextTick(async () => {
            await this.loadAndRenderCanvas(this.$root.querySelector('[data-map-story-modal-canvas]'), 'modal');
            const entry = this.maps.get('modal');
            entry?.map.invalidateSize();
            this.focusActiveTarget('modal', false);
        });
    },

    closeMapModal() {
        this.mapModalOpen = false;
        document.documentElement.classList.remove('overflow-hidden');
    },
}));



Alpine.data('cmsContentToc', (config = {}) => ({
    items: [],
    activeId: null,
    open: true,
    observer: null,
    rebuildHandler: null,

    init() {
        this.build();

        this.rebuildHandler = () => {
            this.$nextTick(() => this.build());
        };
        document.addEventListener('livewire:navigated', this.rebuildHandler);
    },

    destroy() {
        this.observer?.disconnect();
        document.removeEventListener('livewire:navigated', this.rebuildHandler);
    },

    build() {
        this.observer?.disconnect();

        const content = document.querySelector(config.contentSelector || '.cms-public-content');

        if (!content) {
            this.items = [];
            this.activeId = null;

            return;
        }

        const usedIds = new Set();
        const headings = Array.from(content.querySelectorAll('h2, h3'))
            .filter((heading) => heading.textContent?.trim());

        headings.forEach((heading) => {
            heading.id = heading.id || this.uniqueHeadingId(heading.textContent, usedIds);
            usedIds.add(heading.id);
            heading.classList.add('cms-heading-anchor');
            heading.title = heading.title || 'Apri link a questa sezione';
            if (heading.dataset.cmsHeadingReady !== 'true') {
                heading.dataset.cmsHeadingReady = 'true';
                heading.addEventListener('click', () => this.scrollTo(heading.id));
            }
        });

        this.items = headings.map((heading) => ({
            id: heading.id,
            text: heading.textContent.trim(),
            level: Number(heading.tagName.replace('H', '')),
        }));
        this.activeId = this.items[0]?.id || null;

        this.observer = new IntersectionObserver((entries) => {
            entries
                .filter((entry) => entry.isIntersecting)
                .sort((left, right) => left.boundingClientRect.top - right.boundingClientRect.top)
                .forEach((entry) => {
                    this.activeId = entry.target.id;
                });
        }, {
            rootMargin: '-20% 0px -70% 0px',
            threshold: 0.01,
        });

        headings.forEach((heading) => this.observer.observe(heading));
    },

    uniqueHeadingId(text, usedIds) {
        const base = (text || 'section')
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '') || 'section';
        let id = base;
        let index = 2;

        while (document.getElementById(id) || usedIds.has(id)) {
            id = `${base}-${index}`;
            index += 1;
        }

        return id;
    },

    scrollTo(id) {
        const heading = document.getElementById(id);

        if (!heading) {
            return;
        }

        const headerOffset = Array.from(document.querySelectorAll('header.sticky, [data-secondary-nav]'))
            .filter((element) => {
                const styles = window.getComputedStyle(element);
                const rect = element.getBoundingClientRect();

                return styles.display !== 'none' && styles.visibility !== 'hidden' && rect.height > 0;
            })
            .reduce((offset, element) => offset + (element.getBoundingClientRect().height || 0), 12);
        const top = window.scrollY + heading.getBoundingClientRect().top - headerOffset - 12;

        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        history.replaceState(null, '', `#${id}`);
        this.activeId = id;
    },
}));

window.Livewire = Livewire;

// Homepage hero (Embla fade carousel) + atlas globe.
const homeHeroInstances = new WeakMap();
const homeGlobeInstances = new WeakMap();

function initHomeHero(root) {
    if (!root || homeHeroInstances.has(root)) return;

    const viewport = root.querySelector('.embla__viewport');
    if (!viewport) return;

    const delay = parseInt(root.dataset.autoplayDelay || '20000', 10);
    const slideCount = root.querySelectorAll('.embla__slide').length;
    if (slideCount === 0) return;
    root.dataset.homeHeroReady = 'true';

    const plugins = [Fade()];
    let autoplayPlugin = null;

    if (slideCount > 1) {
        autoplayPlugin = Autoplay({
            delay,
            stopOnInteraction: false,
            stopOnMouseEnter: false,
            playOnInit: true,
        });
        plugins.push(autoplayPlugin);
    }

    const api = EmblaCarousel(viewport, { loop: slideCount > 1, duration: 35 }, plugins);

    const dots = [...root.querySelectorAll('[data-embla-dot]')];
    const slides = [...root.querySelectorAll('.embla__slide')];
    const counter = root.querySelector('[data-embla-counter]');
    const prepareSlide = (index) => {
        const image = slides[index]?.querySelector('img[data-src]');

        if (!image) {
            return;
        }

        image.src = image.dataset.src;

        if (image.dataset.srcset) {
            image.srcset = image.dataset.srcset;
        }

        delete image.dataset.src;
        delete image.dataset.srcset;
    };
    const updateState = () => {
        const selected = api.selectedScrollSnap();
        prepareSlide(selected);
        dots.forEach((dot, index) => dot.setAttribute('data-active', index === selected ? 'true' : 'false'));
        slides.forEach((slide, index) => slide.setAttribute('data-active', index === selected ? 'true' : 'false'));
        if (counter) {
            counter.textContent = `${String(selected + 1).padStart(2, '0')} / ${String(slideCount).padStart(2, '0')}`;
        }
    };
    dots.forEach((dot) => dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index || '0', 10);
        prepareSlide(index);
        api.scrollTo(index);
    }));
    root.querySelector('[data-embla-prev]')?.addEventListener('click', () => {
        prepareSlide((api.selectedScrollSnap() - 1 + slideCount) % slideCount);
        api.scrollPrev();
    });
    root.querySelector('[data-embla-next]')?.addEventListener('click', () => {
        prepareSlide((api.selectedScrollSnap() + 1) % slideCount);
        api.scrollNext();
    });
    api.on('select', updateState);
    api.on('reInit', updateState);
    updateState();

    if (slideCount > 1) {
        window.setTimeout(() => {
            prepareSlide((api.selectedScrollSnap() + 1) % slideCount);
        }, Math.max(0, delay - 1500));
    }

    // Defensive: re-init after the next paint to pick up final layout dimensions,
    // and explicitly kick the autoplay plugin in case playOnInit ran before layout settled.
    requestAnimationFrame(() => {
        api.reInit();
        if (slideCount > 1) {
            const autoplay = api.plugins()?.autoplay;
            if (autoplay && typeof autoplay.play === 'function') {
                try { autoplay.play(); } catch (_) { /* noop */ }
            }
        }
    });

    homeHeroInstances.set(root, api);
}

function homeGlobeTheme() {
    if (document.querySelector('[data-home-globe-story]')?.closest('.bg-\\[\\#0c0c0d\\]')) {
        return {
            dot: [236, 234, 227],
            ocean: 'rgba(255, 255, 255, 0.08)',
            hairline: 'rgba(255, 255, 255, 0.22)',
            foreground: 'rgb(236, 234, 227)',
            muted: 'rgba(236, 234, 227, 0.78)',
            accent: 'rgb(217, 119, 87)',
            accentSoft: 'rgba(217, 119, 87, 0.34)',
        };
    }

    const isDark = document.documentElement.classList.contains('dark');

    return isDark
        ? {
            dot: [236, 234, 227],
            ocean: 'rgba(255, 255, 255, 0.08)',
            hairline: 'rgba(255, 255, 255, 0.22)',
            foreground: 'rgb(236, 234, 227)',
            muted: 'rgba(236, 234, 227, 0.78)',
            accent: 'rgb(217, 119, 87)',
            accentSoft: 'rgba(217, 119, 87, 0.34)',
        }
        : {
            dot: [63, 48, 40],
            ocean: 'rgba(63, 48, 40, 0.07)',
            hairline: 'rgba(63, 48, 40, 0.24)',
            foreground: 'rgb(63, 48, 40)',
            muted: 'rgba(63, 48, 40, 0.78)',
            accent: 'rgb(143, 65, 40)',
            accentSoft: 'rgba(143, 65, 40, 0.32)',
        };
}

async function loadHomeGlobeTopology() {
    if (!homeGlobeTopologyPromise) {
        homeGlobeTopologyPromise = loadGeoGlobe()
            .then(() => fetch(homeGlobeLandUrl))
            .then((response) => response.json())
            .then((topology) => topojsonClient.feature(topology, topology.objects.land));
    }

    return homeGlobeTopologyPromise;
}

function buildHomeGlobeDots(landFeature) {
    const width = 720;
    const height = 360;
    const offscreen = document.createElement('canvas');
    const context = offscreen.getContext('2d', { willReadFrequently: true });

    offscreen.width = width;
    offscreen.height = height;

    const projection = d3Geo.geoEquirectangular()
        .scale(width / (2 * Math.PI))
        .translate([width / 2, height / 2]);
    const path = d3Geo.geoPath(projection, context);

    context.fillStyle = '#fff';
    context.beginPath();
    path(landFeature);
    context.fill();

    const pixels = context.getImageData(0, 0, width, height).data;
    const isLand = (lng, lat) => {
        const x = Math.floor(((lng + 180) / 360) * width);
        const y = Math.floor(((90 - lat) / 180) * height);

        if (x < 0 || x >= width || y < 0 || y >= height) {
            return false;
        }

        return pixels[(y * width + x) * 4 + 3] > 60;
    };

    const dots = [];
    const total = 14000;
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));

    for (let i = 0; i < total; i += 1) {
        const y = 1 - (i / (total - 1)) * 2;
        const radius = Math.sqrt(1 - y * y);
        const theta = goldenAngle * i;
        const x = Math.cos(theta) * radius;
        const z = Math.sin(theta) * radius;
        const lat = Math.asin(y) * 180 / Math.PI;
        const lng = Math.atan2(z, x) * 180 / Math.PI;

        if (isLand(lng, lat)) {
            dots.push([lng, lat]);
        }
    }

    return dots;
}

async function initHomeGlobe(canvas) {
    if (!canvas || homeGlobeInstances.has(canvas)) return;

    let markers = [];
    try {
        const raw = JSON.parse(canvas.dataset.markers || '[]');
        markers = raw
            .map((marker, index) => ({
                index,
                name: String(marker?.name || ''),
                lat: Number(marker?.lat),
                lng: Number(marker?.lng),
            }))
            .filter((marker) => Number.isFinite(marker.lat) && Number.isFinite(marker.lng));
    } catch (_) {
        markers = [];
    }

    const context = canvas.getContext('2d');
    const storyRoot = canvas.closest('[data-home-globe-story]');
    const storySteps = storyRoot ? [...storyRoot.querySelectorAll('[data-home-globe-step]')] : [];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const state = {
        activeIndex: 0,
        canvas,
        context,
        dpr: Math.min(window.devicePixelRatio || 1, 2),
        dots: [],
        dragging: false,
        frame: null,
        height: 0,
        lastInteract: 0,
        markers,
        observer: null,
        storyCarousel: null,
        onPointerMove: null,
        onPointerUp: null,
        projection: null,
        radius: 0,
        resizeObserver: null,
        rotation: [-12, -16],
        target: markers[0] ? [-markers[0].lng, -markers[0].lat] : [-12, -16],
        width: 0,
    };

    const resize = () => {
        const rect = canvas.getBoundingClientRect();
        const nextWidth = Math.max(1, Math.round(rect.width * state.dpr));
        const nextHeight = Math.max(1, Math.round(rect.height * state.dpr));

        if (nextWidth === state.width && nextHeight === state.height) {
            return;
        }

        state.width = nextWidth;
        state.height = nextHeight;
        canvas.width = nextWidth;
        canvas.height = nextHeight;
        state.radius = Math.min(nextWidth, nextHeight) * 0.42;
        state.projection = d3Geo.geoOrthographic()
            .clipAngle(90)
            .precision(0.5)
            .translate([nextWidth / 2, nextHeight / 2])
            .scale(state.radius);
    };

    const activateMarker = (index) => {
        const marker = markers[index];

        if (!marker) {
            return;
        }

        state.activeIndex = index;
        state.target = [-marker.lng, -marker.lat];
        state.lastInteract = performance.now();

        storySteps.forEach((step, stepIndex) => {
            step.setAttribute('data-active', stepIndex === index ? 'true' : 'false');
        });
    };

    const activateStep = (stepIndex) => {
        const step = storySteps[stepIndex];
        const lat = Number(step?.dataset.lat);
        const lng = Number(step?.dataset.lng);
        const markerIndex = Number.isFinite(lat) && Number.isFinite(lng)
            ? markers.findIndex((marker) => Math.abs(marker.lat - lat) < 0.0001 && Math.abs(marker.lng - lng) < 0.0001)
            : -1;

        storySteps.forEach((storyStep, index) => {
            storyStep.setAttribute('data-active', index === stepIndex ? 'true' : 'false');
        });

        if (markerIndex >= 0) {
            activateMarker(markerIndex);
        }
    };

    const storyCarouselRoot = storyRoot?.querySelector('[data-home-globe-carousel]');
    const storyCarouselViewport = storyCarouselRoot?.querySelector('[data-home-globe-carousel-viewport]');

    if (storyCarouselRoot && storyCarouselViewport && storySteps.length > 0) {
        state.storyCarousel = EmblaCarousel(storyCarouselViewport, {
            align: 'center',
            containScroll: 'trimSnaps',
            loop: storySteps.length > 2,
            skipSnaps: false,
        });

        const syncCarousel = () => {
            activateStep(state.storyCarousel.selectedScrollSnap() % storySteps.length);
        };

        storyCarouselRoot.querySelector('[data-home-globe-carousel-prev]')?.addEventListener('click', () => state.storyCarousel.scrollPrev());
        storyCarouselRoot.querySelector('[data-home-globe-carousel-next]')?.addEventListener('click', () => state.storyCarousel.scrollNext());
        state.storyCarousel.on('select', syncCarousel);
        state.storyCarousel.on('reInit', syncCarousel);
    }

    const draw = () => {
        if (!state.projection || !context) {
            return;
        }

        const theme = homeGlobeTheme();
        const path = d3Geo.geoPath(state.projection, context);
        const centerX = state.width / 2;
        const centerY = state.height / 2;
        const dotSize = Math.max(1.4 * state.dpr, state.radius * 0.006);
        const activeMarker = markers[state.activeIndex];

        context.clearRect(0, 0, state.width, state.height);
        state.projection.rotate(state.rotation);
        const visibleCenter = [-state.rotation[0], -state.rotation[1]];
        const isVisiblePoint = ([lng, lat]) => d3Geo.geoDistance([lng, lat], visibleCenter) <= Math.PI / 2;

        context.beginPath();
        path({ type: 'Sphere' });
        context.fillStyle = theme.ocean;
        context.fill();
        context.lineWidth = 1 * state.dpr;
        context.strokeStyle = theme.hairline;
        context.stroke();

        state.dots.forEach((dot) => {
            if (!isVisiblePoint(dot)) {
                return;
            }

            const point = state.projection(dot);
            if (!point) {
                return;
            }

            const dx = point[0] - centerX;
            const dy = point[1] - centerY;
            const distance = Math.sqrt(dx * dx + dy * dy) / state.radius;
            const alpha = Math.max(0.18, 1 - 0.48 * distance);

            context.fillStyle = `rgba(${theme.dot[0]}, ${theme.dot[1]}, ${theme.dot[2]}, ${alpha.toFixed(3)})`;
            context.fillRect(point[0] - dotSize / 2, point[1] - dotSize / 2, dotSize, dotSize);
        });

        markers.forEach((marker, index) => {
            if (!isVisiblePoint([marker.lng, marker.lat])) {
                return;
            }

            const point = state.projection([marker.lng, marker.lat]);
            if (!point) {
                return;
            }

            const active = index === state.activeIndex;
            const markerRadius = (active ? 5.5 : 4) * state.dpr;

            context.beginPath();
            context.arc(point[0], point[1], markerRadius, 0, Math.PI * 2);
            context.fillStyle = active ? theme.accent : theme.accentSoft;
            context.fill();

            if (active) {
                const pulse = (9 + 3 * Math.sin(performance.now() / 350)) * state.dpr;
                context.beginPath();
                context.arc(point[0], point[1], pulse, 0, Math.PI * 2);
                context.strokeStyle = theme.accentSoft;
                context.lineWidth = 1 * state.dpr;
                context.stroke();

            }

            context.font = `${Math.round((active ? 15 : 13) * state.dpr)}px Georgia, serif`;
            context.fillStyle = active ? theme.foreground : theme.muted;
            context.fillText(marker.name, point[0] + 12 * state.dpr, point[1]);
        });

        if (activeMarker) {
            canvas.setAttribute('aria-label', activeMarker.name);
        }
    };

    const loop = () => {
        if (!state.dragging && !reducedMotion) {
            if (performance.now() - state.lastInteract > 4500) {
                state.rotation[0] -= 0.03;
            } else if (state.target) {
                let deltaLng = state.target[0] - state.rotation[0];
                deltaLng = ((deltaLng + 180) % 360 + 360) % 360 - 180;
                state.rotation[0] += deltaLng * 0.08;
                state.rotation[1] += (state.target[1] - state.rotation[1]) * 0.08;
            }
        }

        draw();
        state.frame = requestAnimationFrame(loop);
    };

    const onPointerDown = (event) => {
        state.dragging = true;
        state.lastInteract = performance.now();
        state.lastPointer = [event.clientX, event.clientY];
        canvas.setPointerCapture?.(event.pointerId);
        canvas.style.cursor = 'grabbing';

        state.onPointerMove = (moveEvent) => {
            if (!state.dragging || !state.lastPointer) {
                return;
            }

            const dx = moveEvent.clientX - state.lastPointer[0];
            const dy = moveEvent.clientY - state.lastPointer[1];
            state.lastPointer = [moveEvent.clientX, moveEvent.clientY];
            state.rotation[0] += dx * 0.3;
            state.rotation[1] = Math.max(-80, Math.min(80, state.rotation[1] - dy * 0.3));
            state.target = [...state.rotation];
        };

        state.onPointerUp = (upEvent) => {
            state.dragging = false;
            state.lastInteract = performance.now();
            canvas.releasePointerCapture?.(upEvent.pointerId);
            canvas.style.cursor = 'grab';
            window.removeEventListener('pointermove', state.onPointerMove);
            window.removeEventListener('pointerup', state.onPointerUp);
        };

        window.addEventListener('pointermove', state.onPointerMove);
        window.addEventListener('pointerup', state.onPointerUp);
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.style.cursor = 'grab';

    state.destroy = () => {
        cancelAnimationFrame(state.frame);
        canvas.removeEventListener('pointerdown', onPointerDown);
        window.removeEventListener('pointermove', state.onPointerMove);
        window.removeEventListener('pointerup', state.onPointerUp);
        state.resizeObserver?.disconnect();
        state.observer?.disconnect();
        state.storyCarousel?.destroy();
    };

    homeGlobeInstances.set(canvas, state);

    const landFeature = await loadHomeGlobeTopology();
    if (!canvas.isConnected) {
        state.destroy();
        homeGlobeInstances.delete(canvas);
        return;
    }

    state.dots = buildHomeGlobeDots(landFeature);
    resize();

    state.resizeObserver = new ResizeObserver(resize);
    state.resizeObserver.observe(canvas);

    if (storySteps.length > 0) {
        activateStep(0);
        if (!state.storyCarousel && 'IntersectionObserver' in window) {
            state.observer = new IntersectionObserver((entries) => {
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

                if (!visible?.target) {
                    return;
                }

                const index = storySteps.indexOf(visible.target);
                if (index >= 0) {
                    activateStep(index);
                }
            }, {
                rootMargin: '-35% 0px -35% 0px',
                threshold: [0.2, 0.45, 0.7],
            });

            storySteps.forEach((step) => state.observer.observe(step));
        }
    }

    loop();
}

function destroyHomeWidgets() {
    document.querySelectorAll('[data-home-hero]').forEach((root) => {
        const api = homeHeroInstances.get(root);
        if (api) {
            api.destroy();
            homeHeroInstances.delete(root);
        }
    });
    document.querySelectorAll('[data-home-globe]').forEach((canvas) => {
        const entry = homeGlobeInstances.get(canvas);
        if (entry) {
            entry.destroy?.();
            homeGlobeInstances.delete(canvas);
        }
    });
}

function initHomeWidgets() {
    document.querySelectorAll('[data-home-hero]').forEach(prepareHomeHero);
    document.querySelectorAll('[data-home-globe]').forEach(prepareHomeGlobe);
    document.querySelectorAll('[data-count-up]').forEach(initCountUp);
}

function prepareHomeHero(root) {
    if (!root || root.dataset.homeHeroPrepared === 'true' || homeHeroInstances.has(root)) {
        return;
    }

    root.dataset.homeHeroPrepared = 'true';
    const activate = () => {
        if (root.isConnected) {
            initHomeHero(root);
        }
    };

    root.addEventListener('pointerenter', activate, { once: true, passive: true });
    root.addEventListener('touchstart', activate, { once: true, passive: true });
    root.addEventListener('focusin', activate, { once: true });
    window.setTimeout(activate, 15000);
}

function prepareHomeGlobe(canvas) {
    if (!canvas || canvas.dataset.homeGlobePrepared === 'true' || homeGlobeInstances.has(canvas)) {
        return;
    }

    canvas.dataset.homeGlobePrepared = 'true';

    if (!('IntersectionObserver' in window)) {
        initHomeGlobe(canvas);

        return;
    }

    const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
            return;
        }

        observer.disconnect();
        initHomeGlobe(canvas);
    }, { rootMargin: '0px', threshold: 0 });

    observer.observe(canvas);
}

function initCountUp(element) {
    if (!element || element.dataset.countUpReady === 'true') {
        return;
    }

    element.dataset.countUpReady = 'true';
    const target = Number(element.dataset.countUp || 0);
    const formatter = new Intl.NumberFormat(document.documentElement.lang || undefined);
    const duration = 900;
    const start = () => {
        const startedAt = performance.now();
        const tick = (now) => {
            const progress = Math.min((now - startedAt) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            element.textContent = formatter.format(Math.round(target * eased));

            if (progress < 1) {
                requestAnimationFrame(tick);
            }
        };

        requestAnimationFrame(tick);
    };

    if (!('IntersectionObserver' in window)) {
        start();
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
            return;
        }

        observer.disconnect();
        start();
    }, { threshold: 0.35 });

    observer.observe(element);
}

document.addEventListener('livewire:navigating', destroyHomeWidgets);
document.addEventListener('livewire:navigated', initHomeWidgets);

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHomeWidgets);
} else {
    initHomeWidgets();
}

requestAnimationFrame(() => {
    requestAnimationFrame(() => Livewire.start());
});
